import { useEffect, useState } from 'react';
import {
  ChiTietDonMuaHang,
  ChiTietDonMuaHangInput,
  ChiTietDonMuaHangResponse,
  DonMuaHang,
  DonMuaHangInput,
  DonMuaHangResponse
} from '@/types/schemas/don-mua-hang-trong-nuoc.type';
import { QUERY_KEYS } from '@/constants/query-keys';
import { useAuth } from '@/contexts/auth-context';
import api from '@/lib/api';

interface UseDonMuaHangTrongNuocReturn {
  donMuaHangs: DonMuaHang[];
  isLoading: boolean;
  isLoadingDetail: boolean;
  addDonMuaHang: (newDonMuaHang: DonMuaHangInput) => Promise<DonMuaHang>;
  updateDonMuaHang: (uuid: string, updatedDonMuaHang: DonMuaHangInput) => Promise<DonMuaHang>;
  deleteDonMuaHang: (uuid: string) => Promise<void>;
  refreshDonMuaHangs: () => Promise<void>;
  getDonMuaHangBySupplier: (supplierId: string) => Promise<DonMuaHang[]>;
  getDonMuaHangByStatus: (status: string) => Promise<DonMuaHang[]>;
  getDonMuaHangByDateRange: (startDate: string, endDate: string) => Promise<DonMuaHang[]>;
  getDonMuaHangByDocumentNumber: (documentNumber: string) => Promise<DonMuaHang | null>;
  getDonMuaHangByContractNumber: (contractNumber: string) => Promise<DonMuaHang[]>;
  getDonMuaHangByEmployee: (employeeId: string) => Promise<DonMuaHang[]>;
  getDonMuaHangDetail: (uuid: string) => Promise<DonMuaHang>;
  searchDonMuaHang: (searchTerm: string) => Promise<DonMuaHang[]>;
  getDonMuaHangByWarehouse: (warehouseId: string) => Promise<DonMuaHang[]>;
  getDonMuaHangByCurrency: (currencyId: string) => Promise<DonMuaHang[]>;
  getActiveDonMuaHangs: () => Promise<DonMuaHang[]>;
  getPendingDonMuaHangs: () => Promise<DonMuaHang[]>;
  getTotalsByStatus: () => Promise<{ [key: string]: number }>;
  // Detail management functions
  addChiTietDonMuaHang: (donMuaHangId: string, chiTiet: ChiTietDonMuaHangInput) => Promise<ChiTietDonMuaHang>;
  updateChiTietDonMuaHang: (uuid: string, chiTiet: ChiTietDonMuaHangInput) => Promise<ChiTietDonMuaHang>;
  deleteChiTietDonMuaHang: (uuid: string) => Promise<void>;
  getChiTietByDonMuaHang: (donMuaHangId: string) => Promise<ChiTietDonMuaHang[]>;
}

/**
 * Hook for managing DonMuaHang (Purchase Order) data
 *
 * This hook provides functions to fetch, create, update, and delete purchase orders.
 * It includes various filtering and search capabilities for purchase order management.
 *
 * @example
 * ```tsx
 * const {
 *   donMuaHangs,
 *   isLoading,
 *   addDonMuaHang,
 *   updateDonMuaHang,
 *   getDonMuaHangBySupplier,
 *   searchDonMuaHang
 * } = useDonMuaHangTrongNuoc();
 *
 * // Create a new purchase order
 * const handleCreate = async (data: DonMuaHangInput) => {
 *   try {
 *     const newOrder = await addDonMuaHang(data);
 *     console.log('Created:', newOrder);
 *   } catch (error) {
 *     console.error('Failed to create:', error);
 *   }
 * };
 *
 * // Search purchase orders
 * const handleSearch = async (term: string) => {
 *   const results = await searchDonMuaHang(term);
 *   console.log('Search results:', results);
 * };
 * ```
 */
export const useDonMuaHangTrongNuoc = (initialDonMuaHangs: DonMuaHang[] = []): UseDonMuaHangTrongNuocReturn => {
  const [donMuaHangs, setDonMuaHangs] = useState<DonMuaHang[]>(initialDonMuaHangs);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isLoadingDetail, setIsLoadingDetail] = useState<boolean>(false);

  const { entity } = useAuth();

  const fetchDonMuaHangs = async () => {
    if (!entity?.slug) return;

    setIsLoading(true);
    try {
      const response = await api.get<DonMuaHangResponse>(`/entities/${entity.slug}/erp/don-mua-hang/`);
      setDonMuaHangs(response.data.results);
    } catch (error) {
      console.error('Error fetching purchase orders:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getDonMuaHangBySupplier = async (supplierId: string): Promise<DonMuaHang[]> => {
    if (!entity?.slug) return [];

    setIsLoading(true);
    try {
      const response = await api.get<DonMuaHangResponse>(
        `/entities/${entity.slug}/erp/don-mua-hang/?ma_ncc=${supplierId}`
      );
      return response.data.results;
    } catch (error) {
      console.error('Error fetching purchase orders by supplier:', error);
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  const getDonMuaHangByStatus = async (status: string): Promise<DonMuaHang[]> => {
    if (!entity?.slug) return [];

    setIsLoading(true);
    try {
      const response = await api.get<DonMuaHangResponse>(`/entities/${entity.slug}/erp/don-mua-hang/?status=${status}`);
      return response.data.results;
    } catch (error) {
      console.error('Error fetching purchase orders by status:', error);
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  const getDonMuaHangByDateRange = async (startDate: string, endDate: string): Promise<DonMuaHang[]> => {
    if (!entity?.slug) return [];

    setIsLoading(true);
    try {
      const response = await api.get<DonMuaHangResponse>(
        `/entities/${entity.slug}/erp/don-mua-hang/?ngay_ct__gte=${startDate}&ngay_ct__lte=${endDate}`
      );
      return response.data.results;
    } catch (error) {
      console.error('Error fetching purchase orders by date range:', error);
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  const getDonMuaHangByDocumentNumber = async (documentNumber: string): Promise<DonMuaHang | null> => {
    if (!entity?.slug) return null;

    setIsLoading(true);
    try {
      const response = await api.get<DonMuaHangResponse>(
        `/entities/${entity.slug}/erp/${QUERY_KEYS.DON_HANG_MUA_TRONG_NUOC}/?so_ct=${documentNumber}`
      );

      if (response.data.results.length > 0) {
        return response.data.results[0];
      }
      return null;
    } catch (error) {
      console.error('Error fetching purchase order by document number:', error);
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const getDonMuaHangByContractNumber = async (contractNumber: string): Promise<DonMuaHang[]> => {
    if (!entity?.slug) return [];

    setIsLoading(true);
    try {
      const response = await api.get<DonMuaHangResponse>(
        `/entities/${entity.slug}/erp/${QUERY_KEYS.DON_HANG_MUA_TRONG_NUOC}/?so_hd=${contractNumber}`
      );
      return response.data.results;
    } catch (error) {
      console.error('Error fetching purchase orders by contract number:', error);
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  const getDonMuaHangByEmployee = async (employeeId: string): Promise<DonMuaHang[]> => {
    if (!entity?.slug) return [];

    setIsLoading(true);
    try {
      const response = await api.get<DonMuaHangResponse>(
        `/entities/${entity.slug}/erp/${QUERY_KEYS.DON_HANG_MUA_TRONG_NUOC}/?ma_nvmh=${employeeId}`
      );
      return response.data.results;
    } catch (error) {
      console.error('Error fetching purchase orders by employee:', error);
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  const addDonMuaHang = async (newDonMuaHang: DonMuaHangInput): Promise<DonMuaHang> => {
    if (!entity?.slug) throw new Error('Entity not found');

    setIsLoading(true);
    try {
      const response = await api.post<DonMuaHang>(`/entities/${entity.slug}/erp/don-mua-hang/`, newDonMuaHang);

      const addedDonMuaHang = response.data;
      setDonMuaHangs(prev => [...prev, addedDonMuaHang]);
      return addedDonMuaHang;
    } catch (error) {
      console.error('Error adding purchase order:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const updateDonMuaHang = async (uuid: string, updatedDonMuaHang: DonMuaHangInput): Promise<DonMuaHang> => {
    if (!entity?.slug) throw new Error('Entity not found');

    setIsLoading(true);
    try {
      const response = await api.put<DonMuaHang>(
        `/entities/${entity.slug}/erp/don-mua-hang/${uuid}/`,
        updatedDonMuaHang
      );

      const updatedData = response.data;
      setDonMuaHangs(prev => prev.map(item => (item.uuid === uuid ? updatedData : item)));
      return updatedData;
    } catch (error) {
      console.error('Error updating purchase order:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const deleteDonMuaHang = async (uuid: string) => {
    if (!entity?.slug) return;

    setIsLoading(true);
    try {
      await api.delete(`/entities/${entity.slug}/erp/don-mua-hang/${uuid}/`);
      setDonMuaHangs(prev => prev.filter(item => item.uuid !== uuid));
    } catch (error) {
      console.error('Error deleting purchase order:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const getDonMuaHangDetail = async (uuid: string): Promise<DonMuaHang> => {
    if (!entity?.slug) throw new Error('Entity not found');

    setIsLoadingDetail(true);
    try {
      const response = await api.get<DonMuaHang>(
        `/entities/${entity.slug}/erp/${QUERY_KEYS.DON_HANG_MUA_TRONG_NUOC}/${uuid}/`
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching purchase order detail:', error);
      throw error;
    } finally {
      setIsLoadingDetail(false);
    }
  };

  const searchDonMuaHang = async (searchTerm: string): Promise<DonMuaHang[]> => {
    if (!entity?.slug) return [];

    setIsLoading(true);
    try {
      const response = await api.get<DonMuaHangResponse>(
        `/entities/${entity.slug}/erp/${QUERY_KEYS.DON_HANG_MUA_TRONG_NUOC}/?search=${encodeURIComponent(searchTerm)}`
      );
      return response.data.results;
    } catch (error) {
      console.error('Error searching purchase orders:', error);
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  const getDonMuaHangByWarehouse = async (warehouseId: string): Promise<DonMuaHang[]> => {
    if (!entity?.slug) return [];

    setIsLoading(true);
    try {
      const response = await api.get<DonMuaHangResponse>(
        `/entities/${entity.slug}/erp/${QUERY_KEYS.DON_HANG_MUA_TRONG_NUOC}/?ma_kho=${warehouseId}`
      );
      return response.data.results;
    } catch (error) {
      console.error('Error fetching purchase orders by warehouse:', error);
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  const getDonMuaHangByCurrency = async (currencyId: string): Promise<DonMuaHang[]> => {
    if (!entity?.slug) return [];

    setIsLoading(true);
    try {
      const response = await api.get<DonMuaHangResponse>(
        `/entities/${entity.slug}/erp/${QUERY_KEYS.DON_HANG_MUA_TRONG_NUOC}/?ma_nt=${currencyId}`
      );
      return response.data.results;
    } catch (error) {
      console.error('Error fetching purchase orders by currency:', error);
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  const getActiveDonMuaHangs = async (): Promise<DonMuaHang[]> => {
    return getDonMuaHangByStatus('1');
  };

  const getPendingDonMuaHangs = async (): Promise<DonMuaHang[]> => {
    return getDonMuaHangByStatus('0');
  };

  const getTotalsByStatus = async (): Promise<{ [key: string]: number }> => {
    if (!entity?.slug) return {};

    setIsLoading(true);
    try {
      const response = await api.get<DonMuaHangResponse>(`/entities/${entity.slug}/erp/don-mua-hang/totals-by-status/`);
      return response.data as any; // API should return status totals
    } catch (error) {
      console.error('Error fetching purchase order totals by status:', error);
      return {};
    } finally {
      setIsLoading(false);
    }
  };

  // Detail management functions
  const addChiTietDonMuaHang = async (
    donMuaHangId: string,
    chiTiet: ChiTietDonMuaHangInput
  ): Promise<ChiTietDonMuaHang> => {
    if (!entity?.slug) throw new Error('Entity not found');

    try {
      const response = await api.post<ChiTietDonMuaHang>(`/entities/${entity.slug}/erp/chi-tiet-don-mua-hang/`, {
        ...chiTiet,
        don_mua_hang: donMuaHangId
      });
      return response.data;
    } catch (error) {
      console.error('Error adding purchase order detail:', error);
      throw error;
    }
  };

  const updateChiTietDonMuaHang = async (uuid: string, chiTiet: ChiTietDonMuaHangInput): Promise<ChiTietDonMuaHang> => {
    if (!entity?.slug) throw new Error('Entity not found');

    try {
      const response = await api.patch<ChiTietDonMuaHang>(
        `/entities/${entity.slug}/erp/chi-tiet-don-mua-hang/${uuid}/`,
        chiTiet
      );
      return response.data;
    } catch (error) {
      console.error('Error updating purchase order detail:', error);
      throw error;
    }
  };

  const deleteChiTietDonMuaHang = async (uuid: string): Promise<void> => {
    if (!entity?.slug) return;

    try {
      await api.delete(`/entities/${entity.slug}/erp/chi-tiet-don-mua-hang/${uuid}/`);
    } catch (error) {
      console.error('Error deleting purchase order detail:', error);
      throw error;
    }
  };

  const getChiTietByDonMuaHang = async (donMuaHangId: string): Promise<ChiTietDonMuaHang[]> => {
    if (!entity?.slug) return [];

    try {
      const response = await api.get<ChiTietDonMuaHangResponse>(
        `/entities/${entity.slug}/erp/chi-tiet-don-mua-hang/?don_mua_hang=${donMuaHangId}`
      );
      return response.data.results;
    } catch (error) {
      console.error('Error fetching purchase order details:', error);
      return [];
    }
  };

  useEffect(() => {
    fetchDonMuaHangs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entity?.slug]);

  return {
    donMuaHangs,
    isLoading,
    isLoadingDetail,
    addDonMuaHang,
    updateDonMuaHang,
    deleteDonMuaHang,
    refreshDonMuaHangs: fetchDonMuaHangs,
    getDonMuaHangBySupplier,
    getDonMuaHangByStatus,
    getDonMuaHangByDateRange,
    getDonMuaHangByDocumentNumber,
    getDonMuaHangByContractNumber,
    getDonMuaHangByEmployee,
    getDonMuaHangDetail,
    searchDonMuaHang,
    getDonMuaHangByWarehouse,
    getDonMuaHangByCurrency,
    getActiveDonMuaHangs,
    getPendingDonMuaHangs,
    getTotalsByStatus,
    // Detail management functions
    addChiTietDonMuaHang,
    updateChiTietDonMuaHang,
    deleteChiTietDonMuaHang,
    getChiTietByDonMuaHang
  };
};

export const fetchAllDonMuaHangTrongNuoc = async (entitySlug: string, pageSize = 200): Promise<DonMuaHang[]> => {
  let page = 1;
  let all: DonMuaHang[] = [];
  let totalCount = 0;

  while (true) {
    const response = await api.get<DonMuaHangResponse>(
      `/entities/${entitySlug}/erp/${QUERY_KEYS.DON_HANG_MUA_TRONG_NUOC}/`,
      {
        params: {
          page,
          page_size: pageSize
        }
      }
    );

    const pageResults = response.data.results || [];
    totalCount = response.data.count ?? totalCount;
    all = all.concat(pageResults);

    if (!pageResults.length) {
      break;
    }

    const hasNext = (response.data as any).next;

    if (!hasNext && pageResults.length < pageSize) {
      break;
    }

    if (totalCount && all.length >= totalCount) {
      break;
    }

    page += 1;
  }

  return all;
};
